import { useState } from "react";
import Calibration, { CalibrationNotice, StartNotice, ParamsNotice } from "./components/tonalCalibration";
import EarSelection from "../../components/earSelection/earSelection";
import TonalTestPanel from "./components/tonalTestPanel";
import TestParamsModal from "../../modal/tonalTestParamsModal/tonalTestParamsModal";
import SendEmailResultModal from "../../modal/sendEmailResultModal/sendEmailResultModal";
import TonalResultPanel from "../../components/tonalResultPanel/tonalResultPanel";
import TonalParamsBadges from "../../components/tonalResultPanel/tonalParamsBadges";
import { getApiUrl } from "../../utils/getApiUrl";
import AnalysisModal from "../../modal/analysisModal/analysisModalBase";
import { fetchWithAuth } from "../../auth/auth";
import { useResultExport } from "../../utils/useResultExport";
import { useAuthUser } from "../../utils/useAuthUser";
import { useAnalysisModal } from "../../utils/useAnalysisModal";
import { logActivity } from "../../utils/logActivity";
import type { AddTestResultRequest, HearingTestResultPayload, TestParameters } from "../../type/result";
import type { AgeCategory, Gender, HeadphoneConnection, HeadphoneType } from "../../type/demographics";
import "./tonalAudiometry.scss";
import type { Ear, TonalResetHearingTestStateArgs, TestStep } from "../../type/tonalTest";
import { useOverlayAnimation } from "../../utils/useOverlayAnimation";

const frequencies = [125, 250, 500, 1000, 2000, 4000, 8000];

const resetHearingTestState = ({
  clearOverlayFrame,
  clearFinishTransitionTimeout,
  setTonePanelOverlay,
  setResults,
  setEar,
  setStep
}: TonalResetHearingTestStateArgs) => {
  clearOverlayFrame();
  clearFinishTransitionTimeout();
  setTonePanelOverlay(null);
  setResults({ left: [], right: [] });
  setEar("left");
  setStep("start");
};

export default function TonalAudiometry() {
  const [step, setStep] = useState<TestStep>("start");
  const [ear, setEar] = useState<Ear>("left");
  const [results, setResults] = useState<Record<Ear, number[]>>({
    left: [],
    right: []
  });
  const [savedTestResultId, setSavedTestResultId] = useState<number | null>(null);
  const [addNoise, setAddNoise] = useState(false);
  const [continuous, setContinuous] = useState(false);
  const [randomTest, setRandomTest] = useState(false);
  const [adaptiveTest, setAdaptiveTest] = useState(true);
  const [gender, setGender] = useState<Gender>("unspecified");
  const [ageCategory, setAgeCategory] = useState<AgeCategory>("unspecified");
  const [headphoneType, setHeadphoneType] = useState<HeadphoneType>("unspecified");
  const [headphoneConnection, setHeadphoneConnection] = useState<HeadphoneConnection>("unspecified");
  const [isParamsModalOpen, setIsParamsModalOpen] = useState(false);
  const user = useAuthUser();
  const {
    tonePanelOverlay,
    setTonePanelOverlay,
    animateTonePanelToSource,
    clearOverlayFrame,
    clearFinishTransitionTimeout,
    testPanelSlotRef,
    testPanelShellRef,
    isTonePanelFloating,
    floatingTonePanelStyle,
  } = useOverlayAnimation(step === "test");
  const {
    isExportMenuOpen,
    setIsExportMenuOpen,
    isExporting,
    exportFormat,
    exportMenuRef,
    resultPanelRef,
    handleExport,
    captureResultPanel
  } = useResultExport({
    panelSelector: ".results",
    actionsClassName: "result-header__actions",
    getFileBaseName: () => `hearing-test-result-${new Date().toISOString().slice(0, 10)}`,
    onExported: (format) => logActivity("download_result", window.location.pathname, `Pobrano wyniki audiometrii tonalnej jako ${format.toUpperCase()}`, savedTestResultId)
  });
  //const showAnalysisButton = user?.parameters?.["open-ai"]?.value === true;
  const showAnalysisButton = true;
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const { isAnalysisModalOpen, handleCloseAnalysisModal, handleAnalyzeResults } = useAnalysisModal({
    frequencies,
    left: results.left,
    right: results.right
  }, savedTestResultId);

  const finalizeResultStep = async (finalResults: Record<Ear, number[]>) => {
    setResults(finalResults);
    setStep("result");
    setTonePanelOverlay(null);
    const resultId = await saveResult(finalResults);
    logActivity("end_test", window.location.pathname, "Zakończono audiometrię tonalną", resultId);
  };

  const getTestParameters = (): TestParameters => ({
    addNoise,
    continuous,
    randomTest,
    adaptTest: adaptiveTest,
    gender,
    ageCategory,
    headphoneType,
    headphoneConnection
  });
  
  const saveResult = async (finalResults: Record<Ear, number[]>) => {
    try {
      const resultPayload: HearingTestResultPayload = {
        frequencies,
        left: finalResults.left,
        right: finalResults.right
      };

      const payload: AddTestResultRequest = {
        iduser: user?.iduser || null, // replace with actual user id if logged in
        ip_address: null,
        web_agent: navigator.userAgent,
        result: JSON.stringify(resultPayload),
        test_params: JSON.stringify(getTestParameters())
      };

      const response = await fetchWithAuth(getApiUrl("testresult/add"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (data?.idtonalresult) { setSavedTestResultId(data.idtonalresult); return data.idtonalresult as number; }
    } catch (err) {
      console.error("Failed to save hearing test result:", err);
    }
    return null;
  };

  const handleTestComplete = (data: Record<Ear, number[]>) => {
    if (randomTest) {
      animateTonePanelToSource(() => finalizeResultStep(data));
      return;
    }

    const otherEar: Ear = ear === "left" ? "right" : "left";
    const currentEarResults = data[ear];

    setResults(prev => ({ ...prev, [ear]: currentEarResults }));

    if (results[otherEar].length === 0) {
      setEar(otherEar);
    } else {
      const finalResults: Record<Ear, number[]> = {
        ...results,
        [ear]: currentEarResults
      };
      animateTonePanelToSource(() => finalizeResultStep(finalResults));
    }
  };

  const handleNewTest = () => {
    setAiAnalysis(null);
    resetHearingTestState({
      clearOverlayFrame,
      clearFinishTransitionTimeout,
      setTonePanelOverlay,
      setResults,
      setEar,
      setStep
    });
  };

  const handleCancelTest = async (partialFromPanel: Record<Ear, number[]>) => {
    const merged: Record<Ear, number[]> = {
      left:  results.left.length  > 0 ? results.left  : partialFromPanel.left,
      right: results.right.length > 0 ? results.right : partialFromPanel.right,
    };
    const hasData = merged.left.length > 0 || merged.right.length > 0;
    if (hasData) {
      const resultId = await saveResult(merged);
      logActivity("end_test", window.location.pathname, "Przerwano audiometrię tonalną (wynik częściowy)", resultId);
    } else {
      logActivity("end_test", window.location.pathname, "Przerwano audiometrię tonalną", undefined);
    }
    handleNewTest();
  };

  return (
    <div className={`hearing-test${isTonePanelFloating ? " hearing-test--tone-focus" : ""}`}>
      {isTonePanelFloating && (
        <div
          className={`hearing-test__tone-backdrop${tonePanelOverlay?.animateToTarget ? " hearing-test__tone-backdrop--visible" : ""}`}
          aria-hidden="true"
        />
      )}
      <div className="hearing-test__content">
        <div className="hearing-test__right">
          {step === "start" && (
            <StartNotice onConfirmed={() => {
              logActivity("start_test", window.location.pathname, "Kliknięto 'Rozpocznij badanie'");
              setStep("params");
            }} />
          )}

          {step === "params" && (
            <ParamsNotice
              onConfirmed={() => {
                logActivity("start_test", window.location.pathname, "Kliknięto przycisk 'Dalej': " + gender + ", " + ageCategory + ", " + headphoneType + ", " + headphoneConnection);
                setStep("volume");
              }}
              onOpenParamsModal={() => setIsParamsModalOpen(true)}
              onNewTest={handleNewTest}
              addNoise={addNoise}
              continuous={continuous}
              randomTest={randomTest}
              adaptiveTest={adaptiveTest}
              gender={gender}
              ageCategory={ageCategory}
              headphoneType={headphoneType}
              headphoneConnection={headphoneConnection}
              onChangeGender={setGender}
              onChangeAgeCategory={setAgeCategory}
              onChangeHeadphoneType={setHeadphoneType}
              onChangeHeadphoneConnection={setHeadphoneConnection}
            />
          )}

          {step === "volume" && (
            <CalibrationNotice onConfirmed={() => {
              logActivity("start_test", window.location.pathname, "Potwierdzono ustawienie głośności");
              setStep("calibration");
            }} onNewTest={handleNewTest} />
          )}

          {step === "calibration" && (
            <Calibration
              onDone={() => {
                const params = `addNoise=${addNoise}, continuous=${continuous}, randomTest=${randomTest}, adaptiveTest=${adaptiveTest}`;
                logActivity("start_test", window.location.pathname, `Kliknięto 'Słyszę, przejdź dalej', rozpoczęto test: ${params}`, undefined);
                setStep(randomTest ? "test" : "ear");
              }}
              onNewTest={handleNewTest}
            />
          )}

          {step === "ear" && !randomTest && (
            <EarSelection
              onNewTest={handleNewTest}
              onSelect={(selected) => {
                setEar(selected);
                logActivity("start_test", window.location.pathname, `Wybrano ucho: ${selected === "left" ? "lewe" : "prawe"}`);
                setStep("test");
              }}
            />
          )}

          {step === "test" && (
            <div
              ref={testPanelSlotRef}
              className="hearing-test__test-panel-slot"
              style={isTonePanelFloating ? { height: `${tonePanelOverlay?.placeholderHeight}px` } : undefined}
            >
              <div
                ref={testPanelShellRef}
                className={`hearing-test__test-panel-shell${isTonePanelFloating ? " hearing-test__test-panel-shell--floating" : ""}`}
                style={floatingTonePanelStyle}
              >
                <TonalTestPanel
                  key={`${ear}-${randomTest ? "random" : "single"}-${adaptiveTest ? "adaptive" : "standard"}`}
                  ear={ear}
                  frequencies={frequencies}
                  onComplete={handleTestComplete}
                  onCancel={handleCancelTest}
                  continuous={continuous}
                  addNoise={addNoise}
                  randomMode={randomTest}
                  professionalMode={adaptiveTest}
                  user={user}
                />
              </div>
            </div>
          )}

          {step === "result" && (
            <>
              <TonalResultPanel
                result={results}
                resultPanelRef={resultPanelRef}
                exportMenuRef={exportMenuRef}
                panelClassName="results"
                subtitle={<TonalParamsBadges params={{ addNoise, continuous, randomTest, adaptTest: adaptiveTest, gender, ageCategory, headphoneType, headphoneConnection }} />}
                isExportMenuOpen={isExportMenuOpen}
                isExporting={isExporting}
                exportFormat={exportFormat}
                isExportDisabled={isExporting}
                showAnalysisButton={showAnalysisButton}
                isAnalysisDisabled={isExporting}
                onNewTest={handleNewTest}
                onToggleExportMenu={() => setIsExportMenuOpen(prev => !prev)}
                onExport={(format) => void handleExport(format)}
                onAnalyze={handleAnalyzeResults}
                onEmailResult={() => { setIsExportMenuOpen(false); setIsEmailModalOpen(true); }}
              />

              <AnalysisModal
                isOpen={isAnalysisModalOpen}
                onClose={handleCloseAnalysisModal}
                parsedResult={{
                  frequencies,
                  left: results.left,
                  right: results.right
                }}
                demographics={{ ageCategory, headphoneType }}
                id={savedTestResultId}
                cachedAnalysis={aiAnalysis}
                onAnalysisSaved={(_id, analysis) => setAiAnalysis(analysis)}
              />
            </>
          )}
        </div>
      </div>

      <TestParamsModal
        isOpen={isParamsModalOpen}
        onClose={() => setIsParamsModalOpen(false)}
        addNoise={addNoise}
        continuous={continuous}
        randomTest={randomTest}
        adaptiveTest={adaptiveTest}
        gender={gender}
        ageCategory={ageCategory}
        onToggleAddNoise={() => setAddNoise(prev => !prev)}
        onToggleContinuous={() => setContinuous(prev => !prev)}
        onToggleRandomTest={() => {
          setRandomTest(prev => {
            const nextRandomTest = !prev;
            if (nextRandomTest) setAddNoise(false);
            return nextRandomTest;
          });
        }}
        onToggleAdaptiveTest={() => setAdaptiveTest(prev => !prev)}
        onChangeGender={setGender}
        onChangeAgeCategory={setAgeCategory}
      />

      <SendEmailResultModal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        captureResultPanel={captureResultPanel}
        fileBaseName={`hearing-test-result-${new Date().toISOString().slice(0, 10)}`}
        idtonalresult={savedTestResultId}
      />
    </div>
  );
}